// React
import React, { useMemo } from "react";

// MUI
import { Stack, Box, Alert } from "@mui/material";

// Components
import {
  CardInfo,
  FormikTextField,
  AdminSettingDeleteHeader,
  AddNewIcon,
} from "Common/Components";

// Form Fields
import {
  appointmentLocationField,
  appointmentScheduleField,
  appointmentSuggestionsFields,
  appointmentSuggestionsFieldRows,
} from "../Common/fields";

// Constants
import { schedules } from "Constants/ClinicConstants";

// Styles
import { commonStyles } from "Common/Styles";

const SchedulePage = ({
  formik,
  selectedClient,
  appointmentTypes,
  viewOnly = false,
}) => {
  const fieldMap = useMemo(
    () =>
      Object.fromEntries(
        appointmentSuggestionsFields
          .filter(
            (f) =>
              f.id !== "type" ||
              (Array.isArray(appointmentTypes) && appointmentTypes.length > 0)
          )
          .map((f) => [
            f.id,
            f.id === "type"
              ? {
                  ...f,
                  options: appointmentTypes.map((type) => ({
                    value: type,
                    label: type,
                  })),
                }
              : f,
          ])
      ),
    [appointmentTypes]
  );

  const handleAddSuggestion = () => {
    const newSuggestions = [
      ...(formik.values.appointmentSuggestions || []),
      {
        suggestion: "",
        type: [],
        length: "",
        isVisible: false,
        overrideProviderIds: "",
      },
    ];
    formik.setFieldValue("appointmentSuggestions", newSuggestions);
  };

  const handleRemoveSuggestion = (index) => {
    const newSuggestions = [...formik.values.appointmentSuggestions];
    newSuggestions.splice(index, 1);
    formik.setFieldValue("appointmentSuggestions", newSuggestions);
    // Reset touched and errors for appointmentSuggestions
    formik.setTouched({
      ...formik.touched,
      appointmentSuggestions: [],
    });
    formik.setErrors({
      ...formik.errors,
      appointmentSuggestions: [],
    });
  };

  const handleIsVisibleChange = (index) => {
    const newSuggestions = [...formik.values.appointmentSuggestions];
    newSuggestions[index].isVisible = !newSuggestions[index].isVisible;
    formik.setFieldValue("appointmentSuggestions", newSuggestions);
  };

  return (
    <>
      {selectedClient?.appointmentLocations.length > 0 && (
        <CardInfo
          title="Location"
          tooltipInfo="Choose the location of the clinic"
        >
          {appointmentLocationField.map((field) => (
            <FormikTextField
              key={field.id}
              formik={formik}
              field={{
                ...field,
                ...(viewOnly && { disabled: true, review: true }),
                options: selectedClient.appointmentLocations.map((item) => ({
                  value: item.location.en,
                  label: item.location.en,
                })),
              }}
              name={field.id}
            />
          ))}
        </CardInfo>
      )}

      <CardInfo
        title="Select Schedule"
        tooltipInfo="Select a client associated with this clinic"
      >
        {appointmentScheduleField.map((field) => (
          <FormikTextField
            key={field.id}
            formik={formik}
            field={{
              ...field,
              ...(viewOnly && { disabled: true, review: true }),
              options: schedules,
            }}
            name={field.id}
          />
        ))}
      </CardInfo>

      <CardInfo
        title="Suggestions"
        tooltipInfo="These suggestions are when the patients can see the availability of the clinic"
        addButtonTitle={!viewOnly && "Add Suggestion"}
        addButtonOnClick={handleAddSuggestion}
      >
        {formik.touched.appointmentSuggestions &&
          typeof formik.errors.appointmentSuggestions === "string" && (
            <Alert severity="error">
              {formik.errors.appointmentSuggestions}
            </Alert>
          )}
        {(formik.values.appointmentSuggestions || []).map((_, index) => (
          <Box key={index} sx={commonStyles.fieldArrayRow}>
            <AdminSettingDeleteHeader
              name="Suggestion"
              index={index}
              onDeleteClick={() => handleRemoveSuggestion(index)}
              isVisible={formik.values.appointmentSuggestions[index].isVisible}
              onVisibleClick={() => handleIsVisibleChange(index)}
              viewOnly={viewOnly}
            />
            {appointmentSuggestionsFieldRows.map((row, rowIdx) => (
              <Stack
                direction="row"
                alignItems="flex-start"
                gap={2}
                key={rowIdx}
              >
                {row.map((fieldId) => {
                  const field = fieldMap[fieldId];
                  if (!field) return null; // Skip if field is not defined
                  return (
                    <FormikTextField
                      key={field.id}
                      formik={formik}
                      field={{
                        ...field,
                        ...(viewOnly && { disabled: true, review: true }),
                      }}
                      name={`appointmentSuggestions.${index}.${field.id}`}
                    />
                  );
                })}
              </Stack>
            ))}
          </Box>
        ))}
        {!viewOnly &&
          (formik.values.appointmentSuggestions?.length || 0) > 0 && (
            <AddNewIcon
              onClick={handleAddSuggestion}
              tooltipInfo="Add a new suggestion"
            />
          )}
      </CardInfo>
    </>
  );
};

export default SchedulePage;
